// engine-exception: auth/onboarding flow, not a resource screen — stays hand-written (blueprint §1: web/src/pages is legacy)
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { ApiError } from '../lib/api';
import { AuthCard, AuthScene } from '../components/authui';
import { SignedInAs } from './OnboardingGate';
import { INVITE_TOKEN_KEY, useInvitePreview } from '../lib/invite';
import { federatedSession } from '../lib/federatedSession';

// Accept a team invite. The link is /invite?token=…. An invite is how an account is made for an
// organisation that already exists (one person belongs to one organisation), so the signed-out
// page names the organisation and the invited email (GET /auth/invite/:token) and goes straight to
// creating THAT account — /login?invite=… locks the email and returns here — or, for an
// organisation that signs in with Halo, to its Halo. GoTrue owns identity; our API redeems the
// token against the authenticated user, whose email must match. Org founding is never shown.

// Friendly copy for the Problem titles the accept endpoint can return.
const ERROR_MESSAGES: Record<string, string> = {
  invalid_invite: 'This invite link is not valid.',
  invite_expired: 'This invite has expired. Ask your administrator to send a new one.',
  invite_used: 'This invite has already been used.',
  invite_email_mismatch:
    'This invite was sent to a different email address. Sign in with the invited email to accept it.',
  already_in_tenant: 'You already belong to an organization.',
};

export function AcceptInvitePage() {
  const { session, api } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const token = params.get('token') ?? (typeof localStorage !== 'undefined' ? localStorage.getItem(INVITE_TOKEN_KEY) : null);

  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const attempted = useRef(false);

  // Not signed in yet: keep the token for after login, then point them at sign-in.
  useEffect(() => {
    if (!session && token) localStorage.setItem(INVITE_TOKEN_KEY, token);
  }, [session, token]);

  // Signed in + have a token: redeem it exactly once.
  useEffect(() => {
    if (!session || !token || attempted.current) return;
    attempted.current = true;
    setAccepting(true);
    setError(null);
    (async () => {
      try {
        const res = await api.request<{ state?: string; tenant_id?: string }>('/v1/invites/accept', {
          method: 'POST',
          body: JSON.stringify({ token }),
        });
        localStorage.removeItem(INVITE_TOKEN_KEY);
        // Membership is derived server-side (GET /v1/onboarding reads it from the DB), so we
        // just invalidate and let the gate route us — whatever state the backend reports.
        await queryClient.invalidateQueries({ queryKey: ['onboarding'] });
        navigate('/', { replace: true });
        void res;
        return;
      } catch (e) {
        localStorage.removeItem(INVITE_TOKEN_KEY);
        if (e instanceof ApiError) {
          const title = (e.problem as { title?: string } | undefined)?.title;
          setError((title && ERROR_MESSAGES[title]) || e.message || 'Could not accept this invite.');
        } else {
          setError(e instanceof Error ? e.message : 'Could not accept this invite.');
        }
        setAccepting(false);
      }
    })();
  }, [session, token, api, navigate, queryClient]);

  // Missing token entirely.
  if (!token) {
    return (
      <AuthScene>
        <AuthCard title="Invite link incomplete" subtitle="This invite link is missing its token.">
          <p className="muted" style={{ textAlign: 'center' }}>Ask your administrator to resend the invitation.</p>
          <Link className="btn block lift" style={{ marginTop: 'var(--sp-3)' }} to="/login">
            Go to sign in
          </Link>
        </AuthCard>
      </AuthScene>
    );
  }

  // Not signed in: this invite IS the account to create, for this organisation.
  if (!session) return <InviteWelcome token={token} />;

  // Signed in: accepting / error.
  return (
    <AuthScene>
      <AuthCard title="Joining the team" footer={<SignedInAs />}>
        {accepting && !error && (
          <div style={{ textAlign: 'center', padding: 'var(--sp-3) 0' }}>
            <div className="progress-line" style={{ maxWidth: 200, margin: '0 auto var(--sp-3)' }} />
            <p className="muted">Accepting your invite…</p>
          </div>
        )}
        {error && (
          <>
            <div className="auth-msg err">⚠ {error}</div>
            <button className="btn primary block lift" style={{ marginTop: 'var(--sp-3)' }} onClick={() => navigate('/', { replace: true })}>
              Continue
            </button>
          </>
        )}
      </AuthCard>
    </AuthScene>
  );
}

function InviteWelcome({ token }: { token: string }) {
  const preview = useInvitePreview(token);
  const [busy, setBusy] = useState(false);
  const [haloError, setHaloError] = useState<string | null>(null);
  const q = (mode: 'signup' | 'signin') => `/login?invite=${encodeURIComponent(token)}&mode=${mode}`;

  if (preview.isLoading) {
    return (
      <AuthScene>
        <AuthCard>
          <div className="progress-line" style={{ maxWidth: 200, margin: 'var(--sp-3) auto' }} />
        </AuthCard>
      </AuthScene>
    );
  }
  if (preview.error || !preview.data) {
    const title = preview.error instanceof ApiError ? (preview.error.problem as { title?: string } | undefined)?.title : undefined;
    return (
      <AuthScene>
        <AuthCard title="This invite can’t be used" subtitle={(title && ERROR_MESSAGES[title]) || 'This invite link is not valid.'}>
          <p className="muted" style={{ textAlign: 'center' }}>Ask your administrator to send a new invite.</p>
        </AuthCard>
      </AuthScene>
    );
  }
  const inv = preview.data;
  const startHalo = async () => {
    setBusy(true);
    setHaloError(null);
    try {
      await federatedSession.begin(inv.tenant_id);
    } catch (e) {
      setHaloError(e instanceof Error ? e.message : 'Could not reach Halo.');
      setBusy(false);
    }
  };

  return (
    <AuthScene>
      <AuthCard title={`Join ${inv.organisation}`} subtitle={`You’ve been invited as ${inv.role === 'member' ? 'a member' : `an ${inv.role}`}. Your account will be ${inv.email}.`}>
        {inv.sign_in === 'halo' ? (
          <>
            {haloError && <div className="auth-msg err">⚠ {haloError}</div>}
            <button className="btn primary block lift" disabled={busy} onClick={() => void startHalo()}>
              {busy ? 'Opening sign-in…' : 'Sign in with SSO'}
            </button>
            <p className="muted" style={{ textAlign: 'center', marginTop: 'var(--sp-3)', fontSize: 'var(--fs-12)' }}>
              {inv.organisation} signs in with single sign-on (its Halo). Use your Halo login for {inv.email}.
            </p>
          </>
        ) : (
          <>
            <Link className="btn primary block lift" to={q('signup')}>
              Create your account
            </Link>
            <Link className="btn block" style={{ marginTop: 'var(--sp-2)' }} to={q('signin')}>
              I already have an account
            </Link>
          </>
        )}
      </AuthCard>
    </AuthScene>
  );
}
